if (!customElements.get('bemalt-whatsapp-floating-action')) {
  customElements.define(
    'bemalt-whatsapp-floating-action',
    class BemaltWhatsappFloatingAction extends HTMLElement {
      connectedCallback() {
        const link = this.querySelector('[data-bemalt-whatsapp-floating-link]');
        const whatsappNumber = (this.dataset.bemaltWhatsappNumber || '').replace(/\D/g, '');

        if (!link || !/^\d{8,15}$/.test(whatsappNumber)) return;

        this.disconnectObservers();
        this.cancelVisibilityTransition();
        const isNewLink = this.link !== link;
        this.link = link;
        if (isNewLink || this.linkTabIndex === undefined) this.linkTabIndex = link.getAttribute('tabindex');
        this.overlappingCartControls = new Set();
        this.cartControlOverlaps = false;
        this.shouldBeVisible = !this.hidden;
        this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
        this.onNativeStateChange = this.updateVisibility.bind(this);
        this.onViewportChange = this.handleViewportChange.bind(this);

        const message = `Hola, me gustaría recibir información sobre los productos y servicios de Bemalt.\n\nPágina que estoy consultando:\n${location.href}`;
        link.href = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';

        document.addEventListener('toggle', this.onNativeStateChange, true);
        document.body.addEventListener('modalClosed', this.onNativeStateChange);
        this.mobileMediaQuery.addEventListener('change', this.onViewportChange);
        window.addEventListener('resize', this.onViewportChange);

        this.refreshOverlayStateObservers();
        this.observeDynamicOverlays();
        this.observeDynamicCartItems();
        this.refreshCartItemsObserver();
        this.updateVisibility();
      }

      disconnectedCallback() {
        document.removeEventListener('toggle', this.onNativeStateChange, true);
        document.body.removeEventListener('modalClosed', this.onNativeStateChange);
        this.mobileMediaQuery?.removeEventListener('change', this.onViewportChange);
        window.removeEventListener('resize', this.onViewportChange);
        this.disconnectObservers();
        this.cancelVisibilityTransition();
        this.restoreDisconnectedState();
      }

      restoreDisconnectedState() {
        if (!this.link) return;
        this.shouldBeVisible = true;
        this.hidden = false;
        this.classList.remove('is-hiding', 'is-showing');
        this.setInteractive(true);
      }

      disconnectObservers() {
        this.overlayStateObserver?.disconnect();
        this.dynamicOverlayObserver?.disconnect();
        this.dynamicCartItemsObserver?.disconnect();
        this.cartItemsObserver?.disconnect();
        this.cartRemoveObserver?.disconnect();
        this.overlayStateObserver = undefined;
        this.dynamicOverlayObserver = undefined;
        this.dynamicCartItemsObserver = undefined;
        this.cartItemsObserver = undefined;
        this.cartRemoveObserver = undefined;
      }

      refreshOverlayStateObservers() {
        this.overlayStateObserver?.disconnect();
        const overlays = document.querySelectorAll('cart-drawer, modal-dialog, product-modal');

        if (!overlays.length) return;

        this.overlayStateObserver = new MutationObserver(this.onNativeStateChange);
        overlays.forEach((overlay) => {
          this.overlayStateObserver.observe(overlay, { attributes: true, attributeFilter: ['class', 'open'] });
        });
      }

      observeDynamicOverlays() {
        this.dynamicOverlayObserver = new MutationObserver((records) => {
          const overlaysChanged = records.some((record) =>
            [...record.addedNodes, ...record.removedNodes].some(
              (node) => node.nodeType === Node.ELEMENT_NODE && this.nodeContainsInterface(node, 'cart-drawer, modal-dialog, product-modal')
            )
          );

          if (!overlaysChanged) return;
          this.refreshOverlayStateObservers();
          this.updateVisibility();
        });

        this.dynamicOverlayObserver.observe(document.body, { childList: true });
      }

      observeDynamicCartItems() {
        const main = document.querySelector('main');
        if (!main) return;

        this.dynamicCartItemsObserver = new MutationObserver((records) => {
          const cartItemsChanged = records.some((record) =>
            [...record.addedNodes, ...record.removedNodes].some(
              (node) => node.nodeType === Node.ELEMENT_NODE && this.nodeContainsInterface(node, 'cart-items')
            )
          );

          if (cartItemsChanged) this.refreshCartItemsObserver();
        });

        this.dynamicCartItemsObserver.observe(main, { childList: true, subtree: true });
      }

      nodeContainsInterface(node, selector) {
        return node.matches(selector) || Boolean(node.querySelector(selector));
      }

      refreshCartItemsObserver() {
        this.cartItemsObserver?.disconnect();
        this.cartItems = document.querySelector('cart-items');

        if (this.cartItems) {
          this.cartItemsObserver = new MutationObserver(() => this.scheduleCartRemoveObserver());
          this.cartItemsObserver.observe(this.cartItems, {
            attributes: true,
            attributeFilter: ['class'],
            childList: true,
            subtree: true,
          });
        }

        this.setupCartRemoveObserver();
      }

      scheduleCartRemoveObserver() {
        if (this.cartObserverRefreshScheduled) return;

        this.cartObserverRefreshScheduled = true;
        queueMicrotask(() => {
          this.cartObserverRefreshScheduled = false;
          if (this.isConnected) this.setupCartRemoveObserver();
        });
      }

      setupCartRemoveObserver() {
        this.cartRemoveObserver?.disconnect();
        this.cartRemoveObserver = undefined;
        this.overlappingCartControls = new Set();
        this.cartControlOverlaps = false;

        if (this.mobileMediaQuery?.matches && this.cartItems) {
          const bounds = this.getFloatingBounds();
          const removeButtons = this.cartItems.querySelectorAll('cart-remove-button:not(.hidden)');

          if (bounds.width && bounds.height && removeButtons.length) {
            removeButtons.forEach((button) => {
              if (this.rectanglesIntersect(bounds, button.getBoundingClientRect())) {
                this.overlappingCartControls.add(button);
              }
            });
            this.cartControlOverlaps = this.overlappingCartControls.size > 0;

            const rootMargin = `${-bounds.top}px ${-(window.innerWidth - bounds.right)}px ${-(window.innerHeight - bounds.bottom)}px ${-bounds.left}px`;
            let observer;
            observer = new IntersectionObserver(
              (entries) => {
                if (this.cartRemoveObserver !== observer) return;

                entries.forEach((entry) => {
                  if (entry.isIntersecting) {
                    this.overlappingCartControls.add(entry.target);
                  } else {
                    this.overlappingCartControls.delete(entry.target);
                  }
                });
                this.cartControlOverlaps = this.overlappingCartControls.size > 0;
                this.updateVisibility();
              },
              { rootMargin, threshold: 0 }
            );
            this.cartRemoveObserver = observer;
            removeButtons.forEach((button) => observer.observe(button));
          }
        }

        this.updateVisibility();
      }

      getFloatingBounds() {
        const wasHidden = this.hidden;
        if (wasHidden) this.hidden = false;
        try {
          return this.getBoundingClientRect();
        } finally {
          if (wasHidden) this.hidden = true;
        }
      }

      rectanglesIntersect(first, second) {
        return first.left < second.right && first.right > second.left && first.top < second.bottom && first.bottom > second.top;
      }

      handleViewportChange() {
        this.setupCartRemoveObserver();
      }

      hasPriorityOverlay() {
        return Boolean(
          document.querySelector(
            'header-drawer > details[open], menu-drawer > details[open], cart-drawer.active, modal-dialog[open], product-modal[open]'
          )
        );
      }

      updateVisibility() {
        const shouldHide = this.mobileMediaQuery?.matches && (this.hasPriorityOverlay() || this.cartControlOverlaps);
        this.setVisibility(!shouldHide);
      }

      setVisibility(shouldBeVisible) {
        const stateMatches =
          shouldBeVisible === this.shouldBeVisible &&
          (shouldBeVisible
            ? !this.hidden && !this.classList.contains('is-hiding')
            : this.hidden || this.classList.contains('is-hiding'));

        this.shouldBeVisible = shouldBeVisible;
        if (stateMatches) return;

        if (shouldBeVisible) {
          this.show();
        } else {
          this.hide();
        }
      }

      show() {
        this.cancelVisibilityTransition();
        this.hidden = false;

        if (this.prefersReducedMotion()) {
          this.classList.remove('is-hiding', 'is-showing');
          this.setInteractive(true);
          return;
        }

        this.classList.remove('is-hiding');
        this.classList.add('is-showing');
        this.setInteractive(false);
        this.visibilityFrame = requestAnimationFrame(() => {
          this.visibilityFrame = undefined;
          if (!this.shouldBeVisible || !this.isConnected) return;
          this.classList.remove('is-showing');
          this.setInteractive(true);
        });
      }

      hide() {
        if (this.hidden) {
          this.classList.remove('is-hiding', 'is-showing');
          this.setInteractive(false);
          return;
        }

        this.cancelVisibilityTransition();
        this.setInteractive(false);

        if (this.prefersReducedMotion()) {
          this.finishHiding();
          return;
        }

        this.classList.remove('is-showing');
        this.classList.add('is-hiding');
        this.addEventListener('transitionend', this.onTransitionEnd ??= this.handleTransitionEnd.bind(this));
        this.visibilityTimeout = window.setTimeout(() => this.finishHiding(), 220);
      }

      handleTransitionEnd(event) {
        if (event.target === this.link && event.propertyName === 'opacity') this.finishHiding();
      }

      finishHiding() {
        if (this.shouldBeVisible) return;
        this.cancelVisibilityTransition();
        this.classList.remove('is-hiding', 'is-showing');
        this.hidden = true;
        this.setInteractive(false);
      }

      cancelVisibilityTransition() {
        if (this.visibilityFrame) cancelAnimationFrame(this.visibilityFrame);
        if (this.visibilityTimeout) window.clearTimeout(this.visibilityTimeout);
        this.visibilityFrame = undefined;
        this.visibilityTimeout = undefined;
        if (this.onTransitionEnd) this.removeEventListener('transitionend', this.onTransitionEnd);
      }

      setInteractive(isInteractive) {
        if (!this.link) return;
        this.inert = !isInteractive;

        if (isInteractive) {
          this.removeAttribute('aria-hidden');
          if (this.linkTabIndex === null) {
            this.link.removeAttribute('tabindex');
          } else {
            this.link.setAttribute('tabindex', this.linkTabIndex);
          }
        } else {
          this.setAttribute('aria-hidden', 'true');
          if (this.contains(document.activeElement)) document.activeElement.blur();
          this.link.setAttribute('tabindex', '-1');
        }
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }
    }
  );
}
